import React from "react";
import { X } from "lucide-react";
import Input from "../../pagesSpecific/Login/Input";
import Button from "../../Reusable/Button";
import ClickableLinkEmbeddedText from "../../Reusable/ClickableLinkEmbeddedText";
import useForm from "../../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../../constants/fieldTypes";
import { useThunk } from "../../../hooks/useThunk";
import { forgotPassword } from "../../../store/thunks/authThunks";
import showToast from "../../../utils/toastNotifications";
import { useNavigate } from "react-router-dom";

const ForgotPasswordScreen = ({
  isOpen = true,
  onClose,
  variant,
  handlers,
}) => {
  const [doForgotPassword, isForgotPasswordLoading] = useThunk(forgotPassword);
  const navigate = useNavigate();

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
  } = useForm(
    { emailOrPhone: "" },
    { emailOrPhone: true },
    { emailOrPhone: COMMON_FIELD_TYPES.EMAIL_OR_PHONE }
  );

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setTouched({ emailOrPhone: true });

    if (validateForm()) {
      const result = await doForgotPassword(formData);
      if (result?.success) {
        showToast("success", "Reset link sent to your email/phone");
        if (onClose) onClose();
      } else {
        showToast(
          "error",
          result?.error?.message || "Failed to send reset link"
        );
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 flex items-center justify-center w-full max-w-[566px]">
        <div
          className="relative w-full p-6 rounded-2xl sm:p-8"
          style={{
            background: "var(--color-background-01)",
            boxShadow: "var(--form-box-shadow)",
            backdropFilter: "var(--form-backdrop)",
            border: "1px solid var(--color-stroke-01)",
          }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-[var(--color-content-02)] hover:text-[var(--color-content)] transition-colors"
          >
            <X size={20} />
          </button>

          {/* Title */}
          <div className="mb-6 text-center sm:mb-8">
            <h1
              className="font-inter font-bold text-[28px] sm:text-[36px] tracking-[0.02em]"
              style={{
                background: "var(--forgot-pass)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
              }}
            >
              Forgot Password
            </h1>
          </div>

          {/* Form */}
          <div className="flex flex-col items-center space-y-4 w-full max-w-[470px] mx-auto">
            <Input
              label="Email or Phone Number"
              id="emailOrPhone"
              name="emailOrPhone"
              type="text"
              placeholder="Enter Your Email Or Phone Number"
              value={formData.emailOrPhone}
              onChange={(e) =>
                handleInputChange("emailOrPhone", e.target.value)
              }
              onBlur={() => handleBlur("emailOrPhone", formData.emailOrPhone)}
              onFocus={() => clearError("emailOrPhone")}
              error={touched.emailOrPhone && errors.emailOrPhone}
              isRequired
              inputStyle="w-full h-[41px] p-[12px] rounded-[6px] border-[0.6px] bg-transparent text-[var(--color-content-03)]"
              labelStyle="font-inter font-medium text-sm text-[var(--color-content-01)]"
            />

            <Button
              onClick={handleSubmit}
              disabled={isForgotPasswordLoading || !formData.emailOrPhone}
              variant="login2"
              loading={isForgotPasswordLoading}
              className="w-full h-[51px] rounded-[6px] border border-[var(--color-secondary)] font-bold text-[16px] sm:mt-[72px] text-[#efffff] my-2"
            >
              {isForgotPasswordLoading
                ? "Sending Link..."
                : "Request Reset Link"}
            </Button>

            <div className="flex justify-center w-full">
              <ClickableLinkEmbeddedText
                fullText="Remember Password? Sign In"
                linkedText="Sign In"
                onClick={handlers?.switchToSignIn}
                textColor="var(--color-content-04)"
                linkColor="var(--color-primary)"
                className="font-inter text-[14px] text-center w-full -mt-2 sm:-mt-4"
                textWeight="font-normal"
                linkWeight="font-semibold"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordScreen;
